// Seedable PRNG (mulberry32). Deterministic by design: the same seed produces
// the same run, which is what makes replays and authoritative-server multiplayer
// possible later. Core logic must never call Math.random() directly — pass an
// Rng instead.
export interface Rng {
  /** Float in [0, 1). */
  next(): number;
  /** Float in [min, max). */
  range(min: number, max: number): number;
  /** Integer in [min, max). */
  int(min: number, max: number): number;
  /** Random element of a non-empty array. */
  pick<T>(items: readonly T[]): T;
  /** True with probability `p` (default 0.5). */
  chance(p?: number): boolean;
}

export function createRng(seed: number): Rng {
  let a = seed >>> 0;
  const next = (): number => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return {
    next,
    range: (min, max) => min + next() * (max - min),
    int: (min, max) => Math.floor(min + next() * (max - min)),
    pick: (items) => items[Math.floor(next() * items.length)] as never,
    chance: (p = 0.5) => next() < p,
  };
}

/** A non-deterministic seed for casual single-player sessions. */
export const randomSeed = (): number => (Math.random() * 0xffffffff) >>> 0;
